import { z } from 'zod';

// User schemas
export const ZUser = z.object({
  uuid: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  profile_photo_url: z.string().nullable(),
  created_at: z.string(),
});

export type ZUser = z.infer<typeof ZUser>;

// Household schemas
export const ZHousehold = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  invite_code: z.string(),
  gamification_enabled: z.boolean(),
  daily_task_generation_hour: z.number().nullable(),
  last_hourly_job_run: z.string().nullable(),
  created_at: z.string(),
  created_by_uuid: z.string().uuid().nullable(),
});

export type ZHousehold = z.infer<typeof ZHousehold>;

// UserHousehold schemas
export const ZUserHousehold = z.object({
  uuid: z.string().uuid(),
  user_uuid: z.string().uuid().nullable(),
  household_uuid: z.string().uuid().nullable(),
  role: z.enum(['parent', 'child', 'cleaner']),
  current_streak: z.number(),
  longest_streak: z.number(),
  effort_budget: z.record(z.string(), z.number()).nullable(),
  joined_at: z.string(),
});

export type ZUserHousehold = z.infer<typeof ZUserHousehold>;

// Area schemas
export const ZArea = z.object({
  uuid: z.string().uuid(),
  household_uuid: z.string().uuid().nullable(),
  name: z.string(),
  sort_order: z.number().nullable(),
  created_at: z.string(),
  deleted_at: z.string().nullable(),
  deleted_by: z.string().uuid().nullable(),
});

export type ZArea = z.infer<typeof ZArea>;

// Task schemas
export const ZTask = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  points: z.number().int().min(1).max(5),
  frequency_number: z.number().int().positive(),
  frequency_unit: z.enum(['days', 'weeks', 'months', 'years']),
  last_completed_at: z.string().nullable(),
  area_uuid: z.string().uuid().nullable(),
  household_uuid: z.string().uuid().nullable(),
  created_by_uuid: z.string().uuid().nullable(),
  is_high_priority: z.boolean().nullable(),
  is_active: z.boolean().nullable(),
  default_assigned_to_user_uuid: z.string().uuid().nullable(),
  dirty_score: z.number().nullable(),
  sort_order: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
  deleted_by: z.string().uuid().nullable(),
});

export type ZTask = z.infer<typeof ZTask>;

// TaskCompletion schemas
export const ZTaskCompletion = z.object({
  uuid: z.string().uuid(),
  task_uuid: z.string().uuid().nullable(),
  completed_by_user_uuid: z.string().uuid().nullable(),
  completed_at: z.string().nullable(),
  approved_by_uuid: z.string().uuid().nullable(),
  approved_at: z.string().nullable(),
  points_earned: z.number().int(),
  notes: z.string().nullable(),
  household_uuid: z.string().uuid().nullable(),
  assigned_date: z.string().nullable(),
  assigned_to_user_uuid: z.string().uuid().nullable(),
  created_at: z.string(),
  deleted_at: z.string().nullable(),
  deleted_by: z.string().uuid().nullable(),
});

export type ZTaskCompletion = z.infer<typeof ZTaskCompletion>;

// Realtime payload schemas
export const ZRealtimePayload = z.object({
  eventType: z.enum(['INSERT', 'UPDATE', 'DELETE']),
  table: z.string(),
  new: z.unknown().optional(),
  old: z.unknown().optional(),
});

export type ZRealtimePayload = z.infer<typeof ZRealtimePayload>;

// Error schemas
export const ZError = z.object({
  message: z.string(),
  code: z.string().optional(),
  details: z.unknown().optional(),
});

export type ZError = z.infer<typeof ZError>;

// Frequency unit schema for form validation
export const ZFrequencyUnit = z.enum(['days', 'weeks', 'months', 'years']);
export type ZFrequencyUnit = z.infer<typeof ZFrequencyUnit>;

// Helper function to safely parse unknown data
export function parseUnknownError(error: unknown): string {
  // Try to parse as our error type first
  const errorResult = ZError.safeParse(error);
  if (errorResult.success) {
    return errorResult.data.message;
  }
  
  // If it's an Error instance
  if (error instanceof Error) {
    return error.message;
  }
  
  // If it has a message property
  const withMessage = z.object({ message: z.string() }).safeParse(error);
  if (withMessage.success) {
    return withMessage.data.message;
  }
  
  // Default
  return 'An unknown error occurred';
}

// Welcome-back check-in status (GET chat/welcome-back)
export const ZWelcomeBackResponse = z.object({
  eligible: z.boolean(),
  days_away: z.number(),
  last_active_at: z.string().nullable(),
  overdue_count: z.number(),
  task_count: z.number(),
});

const ZPendingActionPayload = z.object({
  id: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()),
  preview: z.string(),
});

// Welcome-back check-in start (POST chat/welcome-back)
export const ZWelcomeBackStartResponse = z.object({
  conversation_id: z.string(),
  message: z.string(),
  pending_actions: z.array(ZPendingActionPayload).optional(),
});
